import { useQuery } from '@tanstack/react-query';
import { useAuthUserId } from '@lucid/auth';
import { LuppApi } from '../lupp.service';
import { LUPP_API_BASE_PATH, LUPP_QUERY_KEYS } from '../constants.lupp';
import { GetPartnerUsersResponse, UseGetPartnerUsersParams } from './dto.onboarding';
import { partnerUserSchema, PartnerUserValidated } from './validators';

/*
|--------------------------------------------------------------------------
| GET PARTNER USERS
|--------------------------------------------------------------------------
*/

/**
 * Fetches partner users from the backend API with pagination support.
 * Uses authenticated user's Auth0 ID (automatically injected via LuppApi interceptor).
 *
 * @param {UseGetPartnerUsersParams} params - Hook parameters
 * @param {number} [params.page=0] - Page number (0-indexed)
 * @param {number} [params.perPage=10] - Number of items per page
 * @param {boolean} [params.enabled=true] - Whether the query should run
 * @returns {UseQueryResult} React Query result with user data, loading state, and error
 *
 * @example
 * const { data, isLoading, error } = useGetPartnerUsers({
 *   page: 0,
 *   perPage: 25,
 *   enabled: true
 * });
 */
export const useGetPartnerUsers = ({
  page = 0,
  perPage = 10,
  enabled = true,
}: UseGetPartnerUsersParams = {}) => {
  const userId = useAuthUserId();

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PARTNER_USERS, userId, page, perPage],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
      });

      const response = await LuppApi.get<GetPartnerUsersResponse>(
        `${LUPP_API_BASE_PATH}/partners/users?${queryParams.toString()}`,
        {
          extractData: (response) => response, // Return full response
        }
      );

      // Validate each record individually so one malformed user doesn't blank out the whole list
      const rawUsers = Array.isArray(response?.data) ? response.data : [];
      const items = rawUsers.reduce<PartnerUserValidated[]>((acc, rawUser) => {
        const result = partnerUserSchema.safeParse(rawUser);
        if (result.success) {
          acc.push(result.data);
        } else {
          console.error('Skipping invalid partner user record:', result.error);
        }
        return acc;
      }, []);

      return {
        items,
      };
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
};
